import React, {useState, useEffect} from "react"
import {Row} from "react-bootstrap"

import {injectIntl, InjectIntl} from "@irati/utils/lang"
import addOrRemoveFromArray from "@irati/utils/addOrRemoveFromArray"

import Select, {SelectOption} from "./Select"
import filterChipStyles from "./styles/filterChipStyles"
import SelectOptionComponent from "./SelectOption"
import Icon, {IconTypes} from "../Icon"
import Flex from "../Flex"
import Badge from "../Badge"
import {Body} from "../Typography"
import DisabledHeaderOption from "./DisabledHeaderOption"
import mainTheme from "../global/theme"

interface Props extends InjectIntl {
  id?: string,
  options: SelectOption[],
  defaultLabel: string,
  selectedLabel: string,
  icon: string,
  selectedOptions: SelectOption[],
  unselectedOptions: SelectOption[],
  selectedHeader: string,
  unselectedHeader: string,
  nothingSelectedHeader: string,
  cleanUpText?: string,
  onChange?: (uuid: string[]) => void,
  isDisabled?: boolean
}

const FilterChipMultiSelect: React.FC<Props> = (props) => {
  const select = React.useRef()
  const [isInitialLoad, setInitialLoad] = useState(true)
  const [options, setOptions] = useState<SelectOption[]>([])
  const [value, setValue] = useState<string[]>([])

  const search = (option: SelectOption, searchTerm: string) => {
    if (option.value === props.defaultLabel ||
      option.value === props.selectedHeader ||
      option.value === props.unselectedHeader ||
      option.value === props.nothingSelectedHeader) {
      return true
    }
    if (searchTerm) {
      return !!option.label.toLowerCase().includes(searchTerm.toLowerCase())
    }
    return true
  }

  const buildOption =  (optionProps: SelectOption) => {
    const {selectedHeader, unselectedHeader, nothingSelectedHeader} = props
    if (optionProps.value === selectedHeader) {
      return <DisabledHeaderOption {...optionProps} onClearAllClick={resetValue} cleanUpText={props.cleanUpText} />
    }
    if (optionProps.value === nothingSelectedHeader) {
      return <DisabledHeaderOption {...optionProps} useBody />
    }
    if (optionProps.value === unselectedHeader) {
      return <DisabledHeaderOption {...optionProps} />
    }

    const icon = value.find((v) => v === optionProps.value) ? IconTypes.minus : IconTypes.plus
    return <SelectOptionComponent {...optionProps} useBody iconAfter={icon} />
  }

  const updateOptions = () => {
    const selectedOptions = props.options.filter((t) => value.includes(t.value))
    const unselectedOptions = props.options.filter((t) => !value.includes(t.value))

    const selectedHeader = {
      value: props.selectedHeader,
      label: props.formatMessage({id: props.selectedHeader}),
      isDisabled: true}
    const unselectedHeader = {
      value: props.unselectedHeader,
      label: props.formatMessage({id: props.unselectedHeader}),
      isDisabled: true}
    const nothingSelectedHeader = {
      value: props.nothingSelectedHeader,
      label: props.formatMessage({id: props.nothingSelectedHeader}),
      isDisabled: true}

    const opts = selectedOptions?.length
      ? [
        selectedHeader,
        ...selectedOptions,
        unselectedHeader,
        ...unselectedOptions
      ]
      : [
        nothingSelectedHeader,
        unselectedHeader,
        ...unselectedOptions
      ]

    setOptions(opts)

    if (!isInitialLoad && select.current) {
      select.current.onMenuOpen()
    }
    setInitialLoad(false)
  }

  const handleChange = (clickedValue: string) => {
    const selectedValues = addOrRemoveFromArray(value, clickedValue)
    setValue(selectedValues)
    if (props?.onChange) {
      return props.onChange(selectedValues)
    }
  }

  const resetValue = () => {
    setValue([])
    if (props?.onChange) {
      return props.onChange([])
    }
  }

  useEffect(() => {
    updateOptions()
  }, [value])

  const getColor = () => {
    if(props?.isDisabled) {
      return mainTheme.colors.greys.mediumLight
    }
    return select?.current?.state.menuIsOpen
      ? mainTheme.colors.greys.white
      : mainTheme.colors.filterChip
  }

  const getBackgroundColor = () => {
    if(props?.isDisabled) {
      return mainTheme.colors.greys.mediumLight
    }
    return select?.current?.state.menuIsOpen
      ? mainTheme.colors.filterChip
      : mainTheme.colors.greys.white
  }

  const valueToRender = value?.length
    ? {value: props.selectedLabel, label: props.selectedLabel}
    : {value: props.defaultLabel, label: props.defaultLabel}

  return (
    <Row>
      <Select
        selectRef={select}
        components={{
          Option: buildOption,
          SingleValue: (data) => {
            const contentColor = getColor()
            const backgroundColor = getBackgroundColor()

            if (data.children === props.defaultLabel) {
              return <>
                <Icon icon={props.icon} marginRight color={contentColor} />
                <Body small color={contentColor}>{props.formatMessage({id: data.children})}</Body>
              </>
            }
            if (data.children === props.selectedLabel) {
              return <Flex alignItems="center">
                <Badge padding={mainTheme.spacing.half} bgColor={contentColor} color={backgroundColor}>{value.length}</Badge>
                <Body small color={contentColor} marginLeft={mainTheme.spacing.one}>{props.formatMessage({id: props.selectedLabel})}</Body>
              </Flex>
            }
            return null
          }
        }}
        id={props?.id}
        options={options}
        value={valueToRender}
        onChange={handleChange}
        hideLabel
        optionRenderer={buildOption}
        filterOption={search}
        isDisabled={props?.isDisabled}
        flush
        styles={filterChipStyles} />
    </Row>
  )
}

export default injectIntl(FilterChipMultiSelect)
